/**
 * D1 -- what somebody DID to a CAP, said in their words.
 *
 * The history rendered a verb keyed on the state each step landed in,
 * so it described the destination rather than the act: a preparer
 * taking a CAP back off the queue read "started this CAP", and a
 * reviewer's approval read "sent it for review". Both are true of the
 * state; neither is what the person did, and a history that
 * mis-attributes acts is worse than no history because it is read as
 * the record.
 *
 * Derived rather than stored. The transition already carries where it
 * came from, where it went, and which seat the actor held -- enough to
 * say what happened. Adding an `action` column would mean changing a
 * HASH-CHAINED record for something the record already implies.
 */
import type { CAPTransition } from "shared/api/types";

export type HistoryVerb =
  | "created"
  | "submitted"
  | "resubmitted"
  | "approved_forward"
  | "approved_closed"
  | "sent_back"
  | "picked_up";

export function historyVerb(tr: CAPTransition): HistoryVerb {
  const { from_state, to_state, actor_position, to_position } = tr;

  // First step: the CAP came into being.
  if (from_state === null) return "created";

  if (to_state === "draft") {
    // Back to the preparer. WHO moved it is the whole difference: the
    // reviewer sending it back and the preparer taking it back off the
    // queue land in the same state and are opposite acts.
    return actor_position === "preparer" ? "picked_up" : "sent_back";
  }

  if (to_state === "sent_back") return "sent_back";

  if (to_state === "in_review") {
    // B3 -- a reviewer's approval does not change the STATE. It moves
    // the CAP from her queue to the approver's and leaves it
    // `in_review`, so this read it as a submission and the history
    // said Omar "sent it for review" when he had approved it.
    if (actor_position === "reviewer" && to_position === "approver") {
      return "approved_forward";
    }
    // A resubmission is a submission the CAP has seen before; the
    // reader wants to know it has been round once.
    return from_state === "sent_back" ? "resubmitted" : "submitted";
  }

  if (to_state === "approved") {
    // Approved and PASSED ON is a different act from approved and
    // finished, and the queue it lands in is what says which.
    return to_position && to_position !== actor_position
      ? "approved_forward"
      : "approved_closed";
  }

  return "submitted";
}
